import { useState } from "react";
import { BaseOmittedFields, FilterFieldBase, FilterType, useFilterBase } from "./FilterBase";
import { Box, Button, Popover, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PopoverTriggerButton, StyledPopoverBody } from "./FieldPopover";
import { formatDate } from "../../utils/date";
import { DatePicker, StaticDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

export type DatepickerFilter = FilterFieldBase<Date | null> & {
    type: FilterType.DATEPICKER;
    maxDate?: Date;
    minDate?: Date;
};

export const useDatepickerFilter = (
    params: Omit<DatepickerFilter, BaseOmittedFields>
): DatepickerFilter => {
    const filterBase = useFilterBase(params, null);

    return {
        ...params,
        ...filterBase,
        type: FilterType.DATEPICKER,
    };
};

const useDatepickerHook = (props: DatepickerFilter) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    const currentValue: Date | null = props.getValue();

    const openPopover = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const maxDate = props.maxDate ? dayjs(props.maxDate) : undefined;

    const minDate = props.minDate ? dayjs(props.minDate) : undefined;

    const closePopover = () => {
        setAnchorEl(null);
        props.setLocalValue(currentValue);
    };

    const open = Boolean(anchorEl);

    const applyFilter = () => {
        props.applyFilter();
        setAnchorEl(null);
    };

    const resetFilter = () => {
        props.resetFilter();
        setAnchorEl(null);
    };

    return {
        anchorEl,
        openPopover,
        closePopover,
        open,
        applyFilter,
        currentValue,
        resetFilter,
        maxDate,
        minDate,
    };
}

export const DatepickerFilterField = (props: DatepickerFilter) => {
    const theme = useTheme();

    const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    if (onlySmallScreen) {
        return <MobileDatepickerFilterField {...props} />;
    }

    return <DesktopDatepickerFilterField {...props} />;
}

const DesktopDatepickerFilterField = (props: DatepickerFilter) => {
    const theme = useTheme();

    const { placeholder, outlined, localValue } = props;

    const {
        openPopover,
        currentValue,
        anchorEl,
        closePopover,
        applyFilter,
        resetFilter,
        open,
        maxDate,
        minDate,
    } = useDatepickerHook(props);

    const formattedDate = currentValue ? formatDate(currentValue) : null;

    return (
        <>
            <PopoverTriggerButton
                onClick={openPopover}
                placeholder={placeholder}
                value={formattedDate}
                outlined={outlined}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={closePopover}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Box sx={{ minWidth: "240px" }}>
                    <Box
                        sx={{
                            width: "100%",
                            padding: theme.spacing(1),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            paddingY: 2,
                            paddingX: 1,
                        }}
                    >
                        <Typography variant="subtitle2">
                            <strong>{placeholder}</strong>
                        </Typography>
                    </Box>

                    <StyledPopoverBody>
                        <StaticDatePicker
                            orientation="landscape"
                            sx={{
                                ".MuiDialogActions-root": {
                                    display: "none"
                                }
                            }}
                            minDate={minDate}
                            maxDate={maxDate}
                            value={localValue ? dayjs(localValue) : undefined}
                            onChange={(date: unknown) => {
                                if (dayjs.isDayjs(date)) {
                                    props.setLocalValue(date.toDate());
                                }
                            }}
                        />
                    </StyledPopoverBody>

                    <Box padding={1} display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={applyFilter} size="small">
                            Použiť
                        </Button>

                        <Button onClick={resetFilter} size="small" color="secondary">
                            Vymazať
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

const MobileDatepickerFilterField = (props: DatepickerFilter) => {
    const {
        placeholder,
        setLocalValue,
        localValue,
    } = props;

    const { maxDate, minDate } = useDatepickerHook(props);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                padding: (theme) => theme.spacing(2),
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}>
                <Typography variant="subtitle2">
                    <strong>{placeholder}</strong>
                </Typography>

                {localValue && (
                    <Button onClick={() => setLocalValue(null)} variant="text">
                        Vymazať
                    </Button>
                )}
            </Box>

            <DatePicker
                maxDate={maxDate}
                minDate={minDate}
                format="DD.MM.YYYY"
                sx={{ width: { xs: "100%", md: 320 } }}
                value={localValue ? dayjs(localValue) : undefined}
                onChange={(date: unknown) => {
                    if (dayjs.isDayjs(date)) {
                        props.setLocalValue(date.toDate());
                    }
                }}
            />
        </Box>
    );
};